/**
 * The two-step upload: ask for a ticket, then PUT the bytes at it.
 *
 * The bytes never pass through the API's JSON layer. The ticket *is* the authorisation --
 * same shape as an S3 presigned URL, which is what it becomes when the deployment moves
 * off local disk -- so the PUT carries no bearer token and occupies no API worker.
 */

import { API_PREFIX, ApiError, apiGet, apiUrl } from '@/api/client'

export const UPLOAD_PURPOSES = [
  'visit_photo',
  'product_image',
  'signature',
  'customer_document',
  'import_file',
  'company_logo',
  'candidate_resume',
] as const

export type UploadPurpose = (typeof UPLOAD_PURPOSES)[number]

export interface UploadTicket {
  key: string
  url: string
  method: string
  content_type: string
  max_bytes: number
  expires_at: number
}

export const requestUploadTicket = (
  purpose: UploadPurpose,
  filename: string,
  content_type?: string,
) =>
  apiGet<UploadTicket>(`${API_PREFIX}/files/tickets`, {
    method: 'POST',
    body: JSON.stringify({ purpose, filename, content_type }),
  })

/**
 * Uploads one file and returns the key to store on whatever row refers to it.
 *
 * The size is checked here as well as on the server: a 5 MB photo on a rural connection
 * should be refused before it is sent, not after.
 */
export async function uploadFile(file: File, purpose: UploadPurpose): Promise<string> {
  const ticket = await requestUploadTicket(purpose, file.name, file.type || undefined)

  if (file.size > ticket.max_bytes) {
    throw new ApiError(
      413,
      'FILE_TOO_LARGE',
      `That file is ${Math.round(file.size / 1024)} KB. The limit is ${Math.round(
        ticket.max_bytes / 1024,
      )} KB.`,
    )
  }

  const res = await fetch(apiUrl(ticket.url), {
    method: ticket.method,
    headers: { 'Content-Type': ticket.content_type },
    body: file,
  })

  if (!res.ok) {
    throw new ApiError(res.status, 'UPLOAD_FAILED', 'The file could not be uploaded.')
  }
  return ticket.key
}
